/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import Tooltip from '@mui/material/Tooltip'
import Button, { ButtonProps } from '../Button'
import { ButtonVariant } from '../types'
import useTheme from '../../../../lib/hooks/useTheme'
import { baseColor, disabledIconColor, primaryTextColor } from '../../../../lib/styles/colors'
import { pullRightCss } from '../../../../lib/styles/cssStyles'

export enum ColorVariant {
  Disabled = 'disabled',
  Primary = 'primary',
  Secondary = 'secondary'
}

export type IconButtonProps = {
  /** ARIA compliant label */
  ariaLabel: string
  /** Icon color variant */
  colorVariant?: ColorVariant
  /** Whether the button is disabled */
  disabled?: boolean
  /** NavLink prop -- used for '/' link  https://github.com/ReactTraining/react-router/issues/5118 */
  exact?: boolean
  /** Toggle tooltip display when hovering over the button (default: true) */
  displayTooltip?: boolean
  /** Optional override to indicate if a button is active */
  isActive?: boolean
  /** If the button links to an external source */
  isExternalLink?: boolean
  /** Whether the button is part of a Nav menu */
  isNav?: boolean
  /** Tooltip shown when hovering over the button.  If not set, ariaLabel will be used. */
  tooltip?: string
} & ButtonProps

const defaultProps: Omit<IconButtonProps, 'ariaLabel'> = {
  colorVariant: undefined,
  disabled: false,
  displayTooltip: true,
  exact: false,
  isActive: false,
  isExternalLink: false,
  isNav: false,
  tooltip: ''
}

// TODO: if we bring back disabled buttons, we'll need to add back
// height, width, and padding styles here.
const tooltipWrapperCss = css`
  display: inline-block;
`

const iconCss = css`
  width: 100%;
  display: flex;
  align-items: inherit;
  justify-content: inherit;
`

const IconButton = ({
  ariaLabel,
  children,
  color,
  colorVariant,
  disabled,
  displayTooltip,
  isActive,
  isExternalLink,
  pullRight,
  tooltip,
  ...props
}: IconButtonProps) => {
  const { primaryColor } = useTheme()
  const getColorVariant = () => {
    if (disabled) {
      return disabledIconColor
    }

    switch (colorVariant) {
      case ColorVariant.Primary:
        return primaryColor!
      case ColorVariant.Secondary:
        return primaryTextColor
      default:
        return baseColor
    }
  }

  const iconColor = color || getColorVariant()

  // Have to add the extra wrapper around the button to trigger the tooltip
  // then an extra wrapper so we can add the tab index to the div so it's
  // triggered on disabled buttons when tabbing to it with the keyboard
  return displayTooltip ? (
    // MUI adds an aria-label where it has no business adding one, set it to undefined to
    // prevent improper structure issues
    <Tooltip title={tooltip || ariaLabel} aria-label={undefined}>
      <div css={[tooltipWrapperCss, pullRight && pullRightCss]}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div css={tooltipWrapperCss} tabIndex={disabled ? 0 : -1}>
          <Button
            {...props}
            ariaLabel={ariaLabel}
            color={iconColor}
            disabled={disabled}
            isActive={isActive}
            isExternalLink={isExternalLink}
            variant={ButtonVariant.Icon}
          >
            <span css={iconCss}>{children}</span>
          </Button>
        </div>
      </div>
    </Tooltip>
  ) : (
    <div css={[tooltipWrapperCss, pullRight && pullRightCss]}>
      <Button
        {...props}
        ariaLabel={ariaLabel}
        color={iconColor}
        disabled={disabled}
        isActive={isActive}
        isExternalLink={isExternalLink}
        variant={ButtonVariant.Icon}
      >
        <span css={iconCss}>{children}</span>
      </Button>
    </div>
  )
}

IconButton.defaultProps = defaultProps

export default IconButton
